import { FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { compact, isEmpty } from 'lodash';
import { Col, Form, Input, message, Radio, Row } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CARTNER_CONVOS } from '../../../../constants/define/translation';
import { appStyles, defaultImage, formScrolling, selectSelector } from '../../../../styles';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../../utils/azure';
import { ButtonActionPage, SpinLoading } from '../../../atoms';
import { DragDropImageV2 } from '../../../molecules/upload';
import { PreviewCartnerConvos } from '../../../molecules/preview/common/PreviewCartnerConvos';
import {
  postCreateAnswerRepository,
  postCreateQuestionRepository,
  patchUpdateQuestionRepository,
  patchUpdateAnswerRepository,
  deleteAnswerRepository,
} from '../../../../repository/mytm/home/cartnerConvosRepository';
import { QuestionType } from '../../../../types/cartnerConvos';

interface FormCartnerConvosProps {
  record?: QuestionType;
  onRefresh: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
}

const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;

export const FormConvos = (props: FormCartnerConvosProps) => {
  const { t } = useTranslation(CARTNER_CONVOS);
  const { record, onRefresh, onCloseModal, setDataRecord } = props;
  const [form] = Form.useForm();
  const options = record?.options ? JSON.parse(record?.options) : null;
  const defaultValue = {
    title: record?.title,
    imageUrl: record?.imageUrl,
    answerA: record?.answers?.[0]?.answerTitle,
    answerIdA: record?.answers?.[0]?.id,
    subCopyA: record?.answers?.[0]?.answerDescription,
    answerIdB: record?.answers?.[1]?.id,
    answerB: record?.answers?.[1]?.answerTitle,
    subCopyB: record?.answers?.[1]?.answerDescription,
    answerIdC: record?.answers?.[2]?.id,
    answerC: record?.answers?.[2]?.answerTitle,
    subCopyC: record?.answers?.[2]?.answerDescription,
    answerIdD: record?.answers?.[3]?.id,
    answerD: record?.answers?.[3]?.answerTitle,
    subCopyD: record?.answers?.[3]?.answerDescription,
    textColor: options?.textColor || '#000',
  };
  const [isCreateLoading, setCreateLoading] = useState(false);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [messageErrorUploadImage, setMessageErrorUploadImage] = useState<string>();
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [dataForm, setDataForm] = useState<any | undefined>(defaultValue);
  const [duplicates, setDuplicates] = useState<any>([]);
  const [countTitle, setCountTitle] = useState<number>(record?.title?.length || 0);
  const [countAnswerA, setCountAnswerA] = useState<number>(defaultValue?.answerA?.length || 0);
  const [countAnswerB, setCountAnswerB] = useState<number>(defaultValue?.answerB?.length || 0);
  const [countAnswerC, setCountAnswerC] = useState<number>(defaultValue?.answerC?.length || 0);
  const [countAnswerD, setCountAnswerD] = useState<number>(defaultValue?.answerD?.length || 0);
  const [countSubAnswerA, setCountSubAnswerA] = useState<any>(defaultValue?.subCopyA?.length || 0);
  const [countSubAnswerB, setCountSubAnswerB] = useState<any>(defaultValue?.subCopyB?.length || 0);
  const [countSubAnswerC, setCountSubAnswerC] = useState<any>(defaultValue?.subCopyC?.length || 0);
  const [countSubAnswerD, setCountSubAnswerD] = useState<any>(defaultValue?.subCopyD?.length || 0);
  const { TextArea } = Input;
  const maxLength = 42;
  const maxLengthTitle = 78;

  const colorOptions = [
    {
      value: '#000',
      label: 'Black',
    },
    {
      value: '#fff',
      label: 'White',
    },
  ];

  const onSetImageList = useCallback((data: UploadFileToBlobInterface[]) => {
    setImageList(data);
  }, []);

  const onSelectedImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  const handleCheck = (newField: any) => {
    const values = compact([
      newField?.answerA?.trim(),
      newField?.answerB?.trim(),
      newField?.answerC?.trim(),
      newField?.answerD?.trim(),
    ]);

    const occurrences = values.reduce((acc, value) => {
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {});

    const duplicatesFound = Object.keys(occurrences).filter(key => occurrences[key] > 1);

    if (duplicatesFound.length > 0) {
      setDuplicates(duplicatesFound);
    } else {
      setDuplicates([]);
    }
  };

  const onChangeInput = useCallback(
    e => {
      const newField = form.getFieldsValue();
      if (!isEmpty(newField.title)) {
        setCountTitle(newField.title.length);
      } else {
        setCountTitle(0);
      }

      if (!isEmpty(newField.answerA)) {
        setCountAnswerA(newField.answerA.length);
      } else {
        setCountAnswerA(0);
      }

      if (!isEmpty(newField.subCopyA)) {
        setCountSubAnswerA(newField.subCopyA.length);
      } else {
        setCountSubAnswerA(0);
      }

      if (!isEmpty(newField.answerB)) {
        setCountAnswerB(newField.answerB.length);
      } else {
        setCountAnswerB(0);
      }

      if (!isEmpty(newField.subCopyB)) {
        setCountSubAnswerB(newField.subCopyB.length);
      } else {
        setCountSubAnswerB(0);
      }

      if (!isEmpty(newField.answerC)) {
        setCountAnswerC(newField.answerC.length);
      } else {
        setCountAnswerC(0);
      }

      if (!isEmpty(newField.subCopyC)) {
        setCountSubAnswerC(newField.subCopyC.length);
      } else {
        setCountSubAnswerC(0);
      }

      if (!isEmpty(newField.answerD)) {
        setCountAnswerD(newField.answerD.length);
      } else {
        setCountAnswerD(0);
      }

      if (!isEmpty(newField.subCopyD)) {
        setCountSubAnswerD(newField.subCopyD.length);
      } else {
        setCountSubAnswerD(0);
      }

      setDataForm((old: any) => ({
        ...old,
        ...newField,
      }));
      handleCheck(newField);
    },
    [form],
  );

  const saveMultipleAnswer = async (questionId: string, isUpdate = false) => {
    if (dataForm.answerIdA) {
      await patchUpdateAnswerRepository(dataForm.answerIdA, {
        questionId,
        answerTitle: dataForm.answerA.trim(),
        answerDescription: dataForm?.subCopyA ? dataForm?.subCopyA.trim() : '',
      });
    } else {
      await postCreateAnswerRepository({
        questionId,
        answerTitle: dataForm.answerA.trim(),
        answerDescription: dataForm?.subCopyA ? dataForm?.subCopyA.trim() : '',
      }).catch(e => e);
    }

    if (dataForm.answerIdB) {
      await patchUpdateAnswerRepository(dataForm.answerIdB, {
        questionId,
        answerTitle: dataForm.answerB.trim(),
        answerDescription: dataForm?.subCopyB ? dataForm?.subCopyB.trim() : '',
      });
    } else {
      await postCreateAnswerRepository({
        questionId,
        answerTitle: dataForm.answerB.trim(),
        answerDescription: dataForm?.subCopyB ? dataForm?.subCopyB.trim() : '',
      }).catch(e => e);
    }

    if (!isEmpty(dataForm.answerC)) {
      if (dataForm.answerIdC) {
        await patchUpdateAnswerRepository(dataForm.answerIdC, {
          questionId,
          answerTitle: dataForm.answerC.trim(),
          answerDescription: dataForm?.subCopyC ? dataForm?.subCopyC.trim() : '',
        });
      } else {
        await postCreateAnswerRepository({
          questionId,
          answerTitle: dataForm.answerC.trim(),
          answerDescription: dataForm?.subCopyC ? dataForm?.subCopyC.trim() : '',
        }).catch(e => e);
      }
    } else if (isEmpty(dataForm.answerC) && dataForm.answerIdC) {
      await deleteAnswerRepository(dataForm.answerIdC);
    }
    if (!isEmpty(dataForm.answerD)) {
      if (dataForm.answerIdD) {
        await patchUpdateAnswerRepository(dataForm.answerIdD, {
          questionId,
          answerTitle: dataForm.answerD.trim(),
          answerDescription: dataForm?.subCopyD ? dataForm?.subCopyD.trim() : '',
        });
      } else {
        await postCreateAnswerRepository({
          questionId,
          answerTitle: dataForm.answerD.trim(),
          answerDescription: dataForm?.subCopyD ? dataForm?.subCopyD.trim() : '',
        }).catch(e => e);
      }
    } else if (isEmpty(dataForm.answerD) && dataForm.answerIdD) {
      await deleteAnswerRepository(dataForm.answerIdD);
    }

    if (isUpdate) {
      message.success(t('message.success.updateQuestion'));
    } else {
      message.success(t('message.success.createQuestion'));
    }
    onRefresh();
    onCloseModal();
    setDataRecord(null);
    setCreateLoading(false);
  };

  const createQuestionAndAnswer = async (imageUrl: any = null) => {
    const payloadQuestion = {
      title: dataForm?.title,
      imageUrl: imageUrl || '',
      options: { textColor: dataForm?.textColor },
    };

    return postCreateQuestionRepository(payloadQuestion)
      .then(data => {
        if (data && data.id) {
          const questionId = data.id;
          saveMultipleAnswer(questionId).catch(e => e);
        }
      })
      .catch(e => {
        setCreateLoading(false);
        message.error(e?.errorMessage || t('message.error.createQuestion'));
      })
      .finally(() => {
        setCreateLoading(false);
        setDataRecord(null);
      });
  };

  const updateQuestionAndAnswer = async (questionId: string, imageUrl: any = null) => {
    const payloadQuestion = {
      title: dataForm?.title,
      imageUrl: imageUrl || '',
      options: { textColor: dataForm?.textColor },
    };

    return patchUpdateQuestionRepository(questionId, {
      ...payloadQuestion,
    })
      .then(data => {
        saveMultipleAnswer(questionId, true).catch(e => e);
      })
      .catch(e => {
        setCreateLoading(false);
        message.error(e?.errorMessage || t('message.error.updateQuestion'));
      })
      .finally(() => {});
  };

  const uploadImage = (imgListNotUpload: any, imgLink: any) => {
    return uploadFileToBlob(imgListNotUpload, CONFIG_CONTAINER_NAME.UPLOADS)
      .then((images: any[]) => {
        let newImagesCDN = images;
        if (azureCDN) {
          newImagesCDN = images.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
        }
        const imageUrl =
          newImagesCDN && newImagesCDN.length ? newImagesCDN[0] : imgLink.map((v: any) => v?.dataURL)?.[0];

        return imageUrl;
      })
      .catch(() => {
        setCreateLoading(false);
        message.error(t('message.error.uploadImage'));
      })
      .finally(() => {});
  };

  const onFinish = useCallback(
    async (formValue: any) => {
      const imgListNotUpload = imageList.filter(img => !img.isImageURL);
      const imgLink = imageList.filter(img => img.isImageURL);
      if (!isEmpty(duplicates)) {
        message.error(t('message.error.duplicateAnswer'));
        return;
      }

      setCreateLoading(true);
      let imageUrl = null;
      if (!isEmpty(imgListNotUpload)) {
        imageUrl = (await uploadImage(imgListNotUpload, imgLink)) || null;
      } else {
        imageUrl = imgLink.map((v: any) => v?.dataURL)?.[0] || null;
      }
      if (record && record.id) {
        updateQuestionAndAnswer(record.id, imageUrl)
          .then(() => {})
          .catch(() => {});
      } else {
        createQuestionAndAnswer(imageUrl)
          .then(() => {})
          .catch(() => {});
      }
    },
    [record, imageList, dataForm],
  );

  const onValuesChange = (changedValues: any) => {
    for (const key in changedValues) {
      // eslint-disable-next-line no-prototype-builtins
      if (changedValues.hasOwnProperty(key)) {
        let value = changedValues[key];
        if (typeof value === 'string') {
          if (key === 'title') {
            value = value.slice(0, maxLengthTitle);
          } else {
            value = value.slice(0, maxLength);
          }
          form.setFieldsValue({
            [key]: value,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (record && record.imageUrl) {
      const imgList: UploadFileToBlobInterface[] = [{ dataURL: record?.imageUrl, isImageURL: true }];
      setImageList(imgList);
      setSelectedImage(0);
      setDataForm({
        ...defaultValue,
      });
    }
  }, [record]);

  useEffect(() => {
    if (imageList.length) {
      setMessageErrorUploadImage('');
    }
  }, [imageList]);

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form
            form={form}
            layout="vertical"
            initialValues={defaultValue}
            onFinish={onFinish}
            onValuesChange={onValuesChange}
            name="form-CartnerConvos"
          >
            <Row className="form-CartnerConvos-container" gutter={[24, 24]}>
              <StyledFormCartnerConvos span={16}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Form.Item
                      name="textColor"
                      label={t('form.textColor')}
                      rules={[{ required: true, message: t('validate.textColorBlank') }]}
                    >
                      <Radio.Group options={colorOptions} onChange={onChangeInput} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={24} className="position-relative">
                    <Form.Item
                      name="title"
                      label={t('form.title')}
                      rules={[{ required: true, whitespace: true, message: t('validate.titleBlank') }]}
                    >
                      <TextArea
                        placeholder={t('form.title')}
                        rows={3}
                        maxLength={maxLengthTitle}
                        onChange={onChangeInput}
                      />
                    </Form.Item>
                  </Col>
                  <div className="count-item-title">{`${countTitle}/${maxLengthTitle} characters`}</div>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Form.Item
                      className="position-relative"
                      name="answerA"
                      label={t('form.answerA')}
                      rules={[{ required: true, whitespace: true, message: t('validate.answerBlank') }]}
                    >
                      <Input placeholder={t('form.answerA')} maxLength={maxLength} onChange={onChangeInput} />
                    </Form.Item>
                    <div className="count-item">{`${countAnswerA}/${maxLength}`}</div>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="subCopyA" label={t('form.subCopyA')} className="position-relative">
                      <Input placeholder={t('form.subCopyA')} maxLength={maxLength} onChange={onChangeInput} />
                    </Form.Item>
                    <div className="count-item">{`${countSubAnswerA}/${maxLength}`}</div>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Form.Item
                      className="position-relative"
                      name="answerB"
                      label={t('form.answerB')}
                      rules={[{ required: true, whitespace: true, message: t('validate.answerBlank') }]}
                    >
                      <Input placeholder={t('form.answerB')} maxLength={maxLength} onChange={onChangeInput} />
                    </Form.Item>
                    <div className="count-item">{`${countAnswerB}/${maxLength}`}</div>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="subCopyB" label={t('form.subCopyB')} className="position-relative">
                      <Input placeholder={t('form.subCopyB')} maxLength={maxLength} onChange={onChangeInput} />
                    </Form.Item>
                    <div className="count-item">{`${countSubAnswerB}/${maxLength}`}</div>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Form.Item name="answerC" label={t('form.answerC')} className="position-relative">
                      <Input placeholder={t('form.answerC')} maxLength={maxLength} onChange={onChangeInput} />
                    </Form.Item>
                    <div className="count-item">{`${countAnswerC}/${maxLength}`}</div>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="subCopyC" label={t('form.subCopyC')} className="position-relative">
                      <Input placeholder={t('form.subCopyC')} maxLength={maxLength} onChange={onChangeInput} />
                    </Form.Item>
                    <div className="count-item">{`${countSubAnswerC}/${maxLength}`}</div>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Form.Item name="answerD" label={t('form.answerD')} className="position-relative">
                      <Input placeholder={t('form.answerD')} maxLength={maxLength} onChange={onChangeInput} />
                    </Form.Item>
                    <div className="count-item">{`${countAnswerD}/${maxLength}`}</div>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="subCopyD" label={t('form.subCopyD')} className="position-relative">
                      <Input placeholder={t('form.subCopyD')} maxLength={maxLength} onChange={onChangeInput} />
                    </Form.Item>
                    <div className="count-item">{`${countSubAnswerD}/${maxLength}`}</div>
                  </Col>
                </Row>
              </StyledFormCartnerConvos>
              <StyledSelectImageCol span={8}>
                <DragDropImageV2
                  {...{
                    imageList,
                    onSetImageList,
                    messageErrorUploadImage,
                    selectedImage,
                    onSelectedImage,
                    maxImage: 1,
                    nodePreview: (
                      <PreviewCartnerConvos
                        values={{
                          backgroundImage: imageList?.length && imageList[0].dataURL,
                          describeText: t('form.imageUpload'),
                        }}
                        isPreview
                        className="preview-cartner-convos"
                        height="400px"
                      />
                    ),
                  }}
                />
                <BtnContainer>
                  <ButtonActionPage
                    loading={isCreateLoading}
                    text={record ? t('form.btnUpdateCartnerConvos') : t('form.btnCreateCartnerConvos')}
                    id="1"
                    className={
                      imageList && imageList.length ? 'isNotTop btn-submit-form-home' : 'isTop btn-submit-form-home'
                    }
                    htmlType="submit"
                    icon={!record ? <FileAddOutlined /> : undefined}
                  />
                </BtnContainer>
              </StyledSelectImageCol>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 15px;
  }

  & .form-CartnerConvos-container {
    padding: 0 1.5rem 1.5rem 1.5rem;

    .form-CartnerConvos {
      resize: none;
    }

    .count-item {
      position: absolute;
      right: 15px;
      top: 42px;
      font-size: 11px;
      color: #cbcbcb;
    }

    .count-item-title {
      position: absolute;
      left: 24px;
      top: 177px;
      font-size: 11px;
      color: #cbcbcb;
    }

    .position-relative {
      position: relative;
    }

    input {
      padding-right: 40px;
    }
  }

  & .preview-home-product {
    border-radius: 18px;
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  }
`;

const BtnContainer = styled.div`
  display: flex;
  justify-content: end;
  align-items: end;

  & .isTop {
    top: 10px !important;
  }

  .isNotTop {
    top: -10px;
  }

  & .btn-submit-form-home {
    width: 160px;
  }

  & .isTop {
    top: 10px !important;
  }
`;

const StyledSelectImageCol = styled(Col)`
  padding: 0px !important;
  min-height: 400px;
  height: 470px;
  background-color: #fff;
  position: relative;
  ${defaultImage}

  & .preview-cartner-convos {
    background-color: #fff;
  }
`;

const StyledFormCartnerConvos = styled(Col)`
  padding-top: 1rem;
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .add-partner {
    float: right;
    color: #3490ff;
    font-weight: bold;
    font-size: 14px;
    position: absolute;
    right: 0;
    z-index: 9999;
    background-color: transparent;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    display: inline;
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }

  & .ant-upload-span {
    display: none !important;
  }

  & .popover {
    position: absolute;
    z-index: 2;
    bottom: 0;
    left: 10%;
  }

  & .popoverSub {
    position: absolute;
    z-index: 2;
    bottom: 0;
    right: 0;
  }

  & .cover {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  & .sketch-picker {
    cursor: pointer;
  }
`;
